import React from "react"
import CalendarTutorial from "../../components/calendar-tutorial"

const tutorial = {
  title: "Build an After-School Activity Schedule with AI",
  description:
    "Turn homework, sport, and music practice into a weekly calendar. Copy an example prompt, review the events, and share the plan.",
  intro:
    "Put homework, sport, and music practice into one visible plan. This tutorial uses Magic Generate to draft a small after-school schedule, with a review step before anything is added to your calendar.",
  rows: [
    ["Monday", "4:00 pm", "Homework", "30 min"],
    ["Tuesday", "4:30 pm", "Violin practice", "30 min"],
    ["Wednesday", "5:00 pm", "Tennis", "60 min"],
    ["Thursday", "4:00 pm", "Homework", "30 min"],
    ["Friday", "4:30 pm", "Violin practice", "30 min"],
  ],
  prompt:
    "For the week starting Monday [YYYY-MM-DD], create these after-school events:\nMonday 4pm Homework for 30 minutes\nTuesday 4:30pm Violin practice for 30 minutes\nWednesday 5pm Tennis for 1 hour\nThursday 4pm Homework for 30 minutes\nFriday 4:30pm Violin practice for 30 minutes",
  steps: [
    {
      title: "1. Choose the week and your fixed commitments",
      paragraphs: [
        "Start with actual lesson and training times. Then choose where homework and home practice fit, leaving space for travel, food, and rest. The sample timings are placeholders for your own commitments.",
        "Open School Calendar, choose New blank calendar, and name it in the calendar settings. Navigate to the week you want to organise.",
      ],
    },
    {
      title: "2. Describe the schedule in Magic Generate",
      paragraphs: [
        "Open Magic Generate and paste the example prompt. Replace the start date, activity names, times, and durations with your own plan before sending it.",
        "An explicit date and a duration for each activity make the result easier to check. Start with one week rather than a whole term.",
      ],
    },
    {
      title: "3. Review and add the suggested events",
      paragraphs: [
        "Read the generated event list. Check that each event falls on the intended day and has the right start time and duration. Once it matches your plan, choose Add to calendar.",
        "Look across the calendar for overlapping activities and gaps that are too short for travel. Open event details to make any adjustments.",
      ],
    },
    {
      title: "4. Ask about the week or adjust the timing",
      paragraphs: [
        "Try asking “What do I have next week?” to see events from the calendar.",
        "If every event needs to move, try “Move all schedules 1 hour later”. Review the proposed events and confirm only if all of them should change. For one changed lesson, edit that event directly.",
      ],
    },
    {
      title: "5. Share the plan with your family",
      paragraphs: [
        "Choose Publish calendar to create a read-only view. Check the content, set an optional password if needed, and publish. Open the link on another device before sharing it with the people who need the schedule.",
        "After changing an event, use Republish to update the same link. Ask family members to reopen or refresh it to see the latest published plan.",
      ],
    },
  ],
  cta: "Create your after-school calendar",
  relatedUrl: "/use-cases/weekly-classroom-calendar/",
  relatedTitle: "Create a Weekly Classroom Calendar and Share It with Parents",
}

export default function TutorialPage() {
  return <CalendarTutorial {...tutorial} />
}
